import { useApolloClient } from '@apollo/client';
import { useShowCache, useSubscriptionCache } from 'src/apollo/cache';
import { updateUserCache } from 'src/apollo/cache/update';
import { useGetAllFeatureFlags } from 'src/components/feature-flags/useFeatureFlag';
import { usePermissionsContext } from 'src/components/permissions/PermissionsContext';
import { initSegment } from 'src/components/routes/useThirdPartyApps';
import {
  useUpdatePersonalisationMutation,
  PodcastPurpose,
  ExperienceLevel,
} from 'src/generated/graphql';
import useApolloErrorHandler from 'src/hooks/useApolloErrorHandler';

interface UpdatePersonalisationArgs {
  experienceLevel: string;
  podcastPurpose: string;
  heardAboutFrom: string;
  heardAboutFromDetail: string;
}

export default function useUpdatePersonalisation() {
  const client = useApolloClient();
  const subscription = useSubscriptionCache();
  const [updatePersonalisationMutation, { error }] = useUpdatePersonalisationMutation();

  const { permissions } = usePermissionsContext();
  const show = useShowCache();
  const featureFlags = useGetAllFeatureFlags();

  useApolloErrorHandler(error);

  const updatePersonalisation = ({
    experienceLevel,
    podcastPurpose,
    heardAboutFrom,
    heardAboutFromDetail,
  }: UpdatePersonalisationArgs) => {
    const heardAboutFromWithDetail = heardAboutFromDetail
      ? `${heardAboutFrom}: ${heardAboutFromDetail}`
      : heardAboutFrom;

    return updatePersonalisationMutation({
      variables: {
        experienceLevel: experienceLevel as ExperienceLevel,
        podcastPurpose: podcastPurpose as PodcastPurpose,
        heardAboutFrom: heardAboutFromWithDetail,
      },
    }).then(res => {
      if (res.data) {
        updateUserCache({ client, user: res.data.updatePersonalisation.user });
        if (process.env.NODE_ENV === 'production') {
          initSegment(
            res.data.updatePersonalisation.user,
            subscription,
            show,
            permissions,
            featureFlags,
          );
        }
      }
    });
  };

  return updatePersonalisation;
}
